import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Crosshair, Scissors, X } from 'lucide-react';
import { Role } from '@jeket-cloud/types/auth';
import { RoleGate } from '@/components/auth/RoleGate';
import { useClips, useCreateJob, useJobs, useRecording, useResults } from '@/hooks/useProcessing';
import type { Chapter, JobFeature, ProcessingJob } from '@/types/processing';
import RecordingTimeline, { type Selection } from './RecordingTimeline';
import SegmentPlayer from './SegmentPlayer';
import {
  ChaptersPanel,
  ClipsPanel,
  HighlightsPanel,
  RegenerateButton,
  SpeakersPanel,
  SummaryPanel,
  ThumbnailsPanel,
  TranscriptPanel,
  useThumbnails,
} from './ResultPanels';
import {
  Card,
  ErrorState,
  InlineError,
  LoadingState,
  buttonPrimary,
  buttonSecondary,
  formatDuration,
  formatOffset,
  formatWallclock,
  inputClass,
  latestOfKind,
  parseOffset,
  resultsOfKind,
} from './shared';

const TABS = [
  { id: 'summary', label: 'Summary' },
  { id: 'highlights', label: 'Highlights & clips' },
  { id: 'chapters', label: 'Chapters' },
  { id: 'speakers', label: 'Speakers' },
  { id: 'thumbnails', label: 'Thumbnails' },
  { id: 'transcript', label: 'Transcript' },
] as const;
type TabId = (typeof TABS)[number]['id'];

/** processing-service rejects a clip_cut longer than this. */
const MAX_CLIP_S = 30 * 60;

function latestJob(jobs: ProcessingJob[] | undefined, feature: JobFeature): ProcessingJob | undefined {
  return (jobs ?? [])
    .filter((j) => j.feature === feature)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
}

/** Editable `m:ss` offset field, committed on blur / Enter. */
function OffsetField({ label, value, origin, onCommit }: { label: string; value: number | null; origin: number; onCommit: (t: number) => void }) {
  const shown = value == null ? '' : formatOffset(value - origin);
  const [text, setText] = useState(shown);
  useEffect(() => setText(shown), [shown]);
  const commit = () => {
    const off = parseOffset(text);
    if (off != null) onCommit(origin + off);
    else setText(shown);
  };
  return (
    <label className="flex items-center gap-2 text-xs text-jeket-silver-muted">
      {label}
      <input
        aria-label={`${label} point`}
        value={text}
        placeholder="m:ss"
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === 'Enter' && commit()}
        className={`${inputClass} w-24 py-1 tabular-nums`}
      />
    </label>
  );
}

export default function RecordingDetailPage() {
  const { sourceId } = useParams<{ sourceId: string }>();
  const { data: rec, isLoading, isError, error } = useRecording(sourceId);
  const streamId = rec?.streamId;
  const { data: results } = useResults(streamId);
  const { data: clips = [] } = useClips(streamId);
  const { data: jobList } = useJobs(streamId ? { streamId, limit: 100 } : undefined, { enabled: !!streamId });
  const thumbnails = useThumbnails(sourceId);
  const createJob = useCreateJob();

  const [tab, setTab] = useState<TabId>('summary');
  const [playhead, setPlayhead] = useState<number | null>(null);
  const [seek, setSeek] = useState<{ t: number; nonce: number } | null>(null);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [clipTitle, setClipTitle] = useState('');
  const [pendingFeature, setPendingFeature] = useState<JobFeature | null>(null);

  // Time axis: programme coverage, widened to any result outside it.
  const range = useMemo(() => {
    const starts = [rec?.start, ...(results ?? []).map((r) => r.start)].filter((x): x is number => x != null);
    const ends = [rec?.end, ...(results ?? []).map((r) => r.end)].filter((x): x is number => x != null);
    if (starts.length === 0 || ends.length === 0) return null;
    return { start: rec?.start ?? Math.min(...starts), end: rec?.end ?? Math.max(...ends) };
  }, [rec, results]);

  const origin = range?.start ?? 0;
  const videoFlowId = rec?.flows.find((f) => f.kind === 'programme' && f.format.endsWith(':video'))?.flowId;

  const final = latestOfKind(results, 'final_summary')?.data;
  const chapters: Chapter[] = final?.chapters?.length
    ? final.chapters
    : (latestOfKind(results, 'chapters')?.data.chapters ?? []);
  const marks = resultsOfKind(results, 'highlight_marks');
  const diarization = latestOfKind(results, 'diarization')?.data;
  const transcript = resultsOfKind(results, 'transcript');

  const onSeek = useCallback((t: number) => {
    setPlayhead(t);
    setSeek((s) => ({ t, nonce: (s?.nonce ?? 0) + 1 }));
  }, []);

  const onSelect = useCallback(
    (sel: Selection | null) => {
      setSelection(sel);
      if (sel) onSeek(sel.start);
    },
    [onSeek],
  );

  const runFeature = (feature: JobFeature) => {
    if (!streamId || !range) return;
    setPendingFeature(feature);
    createJob.mutate(
      { feature, streamId, start: range.start, end: range.end },
      { onSettled: () => setPendingFeature(null) },
    );
  };

  const cutClip = () => {
    if (!streamId || !selection) return;
    setPendingFeature('clip_cut');
    createJob.mutate(
      {
        feature: 'clip_cut',
        streamId,
        start: selection.start,
        end: selection.end,
        params: clipTitle.trim() ? { title: clipTitle.trim() } : undefined,
      },
      {
        onSuccess: () => {
          setSelection(null);
          setClipTitle('');
        },
        onSettled: () => setPendingFeature(null),
      },
    );
  };

  const regen = (feature: JobFeature, label: string) => (
    <RegenerateButton
      label={label}
      onClick={() => runFeature(feature)}
      pending={pendingFeature === feature}
      error={pendingFeature === null && createJob.variables?.feature === feature ? createJob.error : undefined}
    />
  );

  if (isLoading) return <div className="p-6"><LoadingState label="Loading recording…" /></div>;
  if (isError || !rec) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <BackLink />
        <ErrorState title="Couldn't load this recording" error={error} />
      </div>
    );
  }

  const tooLong = !!selection && selection.end - selection.start > MAX_CLIP_S;
  const cutError = pendingFeature === null && createJob.variables?.feature === 'clip_cut' ? createJob.error : null;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4">
      <BackLink />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-jeket-silver-bright truncate">{rec.label || rec.streamId}</h1>
          <p className="text-xs text-jeket-silver-muted truncate">
            {rec.streamId} · {formatWallclock(rec.start)} · {formatDuration(rec.start != null && rec.end != null ? rec.end - rec.start : null)}
          </p>
        </div>
        {rec.eventId && (
          <Link to={`/audience/events/${rec.eventId}`} className={buttonSecondary}>
            Open event
          </Link>
        )}
      </div>

      <Card className="p-3 sm:p-4 space-y-4">
        <div className="max-w-3xl mx-auto w-full">
          {range ? (
            <SegmentPlayer
              videoFlowId={videoFlowId}
              seekTo={seek?.t ?? range.start}
              seekNonce={seek?.nonce ?? 0}
              onTime={setPlayhead}
            />
          ) : (
            <SegmentPlayer videoFlowId={undefined} seekTo={0} seekNonce={0} onTime={setPlayhead} />
          )}
        </div>

        {range ? (
          <>
            <RecordingTimeline
              start={range.start}
              end={range.end}
              playhead={playhead}
              selection={selection}
              chapters={chapters}
              marks={marks}
              turns={diarization?.turns ?? []}
              clips={clips}
              thumbnailTimes={thumbnails.items.map((t) => t.t)}
              onSeek={onSeek}
              onSelect={onSelect}
            />
            <p className="text-[11px] text-jeket-silver-muted">
              Click to seek, drag across the programme lane to mark in/out, or pick a highlight.
            </p>

            <RoleGate role={Role.SiteOperator}>
              <div className="flex flex-col lg:flex-row lg:items-center gap-3 p-3 rounded-lg bg-jeket-black-elevated border border-jeket-gold/10" data-testid="clip-editor">
                <div className="flex items-center gap-2 text-jeket-gold text-sm">
                  <Scissors size={14} /> Manual clip
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <OffsetField
                    label="In"
                    value={selection?.start ?? null}
                    origin={origin}
                    onCommit={(t) => setSelection({ start: t, end: Math.max(selection?.end ?? t + 30, t + 1) })}
                  />
                  <OffsetField
                    label="Out"
                    value={selection?.end ?? null}
                    origin={origin}
                    onCommit={(t) => setSelection({ start: Math.min(selection?.start ?? t - 30, t - 1), end: t })}
                  />
                  <button
                    type="button"
                    className={buttonSecondary}
                    disabled={playhead == null}
                    onClick={() => playhead != null && setSelection({ start: playhead, end: Math.max(selection?.end ?? playhead + 30, playhead + 1) })}
                  >
                    <Crosshair size={12} /> In at playhead
                  </button>
                  <button
                    type="button"
                    className={buttonSecondary}
                    disabled={playhead == null}
                    onClick={() => playhead != null && setSelection({ start: Math.min(selection?.start ?? playhead - 30, playhead - 1), end: playhead })}
                  >
                    <Crosshair size={12} /> Out at playhead
                  </button>
                  {selection && (
                    <span className={`text-xs tabular-nums ${tooLong ? 'text-jeket-error' : 'text-jeket-silver-muted'}`}>
                      {formatDuration(selection.end - selection.start)}
                      {tooLong && ' — clips are limited to 30 minutes'}
                    </span>
                  )}
                </div>
                <div className="flex flex-1 items-center gap-2">
                  <input
                    aria-label="Clip title"
                    value={clipTitle}
                    onChange={(e) => setClipTitle(e.target.value)}
                    placeholder="Clip title (optional)"
                    className={`${inputClass} flex-1 min-w-0 py-1.5`}
                  />
                  {selection && (
                    <button type="button" onClick={() => setSelection(null)} className="p-1.5 text-jeket-silver-muted hover:text-jeket-silver-bright" title="Clear selection">
                      <X size={14} />
                    </button>
                  )}
                  <button type="button" onClick={cutClip} disabled={!selection || tooLong || pendingFeature === 'clip_cut'} className={buttonPrimary}>
                    <Scissors size={14} />
                    {pendingFeature === 'clip_cut' ? 'Queuing…' : 'Cut clip'}
                  </button>
                </div>
              </div>
              <InlineError error={cutError} />
            </RoleGate>
          </>
        ) : (
          <p className="text-sm text-jeket-silver-muted text-center py-4">No media or results on this recording yet.</p>
        )}
      </Card>

      <div className="flex gap-1 overflow-x-auto border-b border-jeket-gold/20" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={
              tab === t.id
                ? 'px-3 py-2 text-sm whitespace-nowrap text-jeket-gold border-b-2 border-jeket-gold -mb-px'
                : 'px-3 py-2 text-sm whitespace-nowrap text-jeket-silver-muted hover:text-jeket-silver-bright'
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="max-w-4xl">
        {tab === 'summary' && (
          <SummaryPanel
            results={results}
            origin={origin}
            onSeek={onSeek}
            latestJob={latestJob(jobList?.jobs, 'final_summary')}
            regenerate={regen('final_summary', final ? 'Regenerate' : 'Generate final summary')}
          />
        )}
        {tab === 'highlights' && (
          <div className="space-y-4">
            <HighlightsPanel marks={marks} clips={clips} origin={origin} onSelect={(start, end) => onSelect({ start, end })} />
            <ClipsPanel clips={clips} origin={origin} onSeek={onSeek} />
          </div>
        )}
        {tab === 'chapters' && (
          <ChaptersPanel chapters={chapters} origin={origin} onSeek={onSeek} regenerate={regen('final_summary', 'Refine with final summary')} />
        )}
        {tab === 'speakers' && <SpeakersPanel diarization={diarization} origin={origin} />}
        {tab === 'thumbnails' && (
          <ThumbnailsPanel
            items={thumbnails.items}
            isLoading={thumbnails.isLoading}
            origin={origin}
            onSeek={onSeek}
            regenerate={regen('thumbnails', thumbnails.items.length ? 'Regenerate' : 'Generate thumbnails')}
          />
        )}
        {tab === 'transcript' && <TranscriptPanel items={transcript} origin={origin} onSeek={onSeek} />}
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/media/recordings" className="inline-flex items-center gap-1 text-sm text-jeket-silver-muted hover:text-jeket-silver-bright">
      <ArrowLeft size={14} /> Recordings
    </Link>
  );
}
